import { mui } from "@/mui";
import { MenuIcon, DownloadIcon, AlertTriangle, Activity, HardDrive, Zap, Globe, Info, ChevronDown, ChevronUp, LoaderCircle, Trees, Pickaxe, ChartNetwork, XIcon } from "lucide-react";
import React, { useState, useContext, useCallback, useEffect } from 'react';
import type { ReactNode } from "react";
import { useFileTransfer, FileTransferContext } from "../provider";
import { formatBytes, formatTimeRemaining, formatTimestamp } from "@/lib/ui-format-utils";
import { FileTransferStateEnum, type FileTransferRecord } from "@/lib/file-transfer-types";
import { getFileFromOPFS, triggerStreamingDownload } from "@/lib/file";
import { memoryMonitor, type MemoryUsage, type FileMemoryEstimate } from "@/lib/memory-monitor";
import { useExtensionState } from "./shared-transfer-components";
import { extensionCapabilityService } from "@/services";
import { raptorQService } from "@/services/raptorq-service";
import { peerMetricsService } from "@/services/peer-metrics-service";

/**
 * Unified filename helper to ensure consistent naming across all download triggers
 * This prevents ghost files by using the same fallback logic everywhere
 */
function getConsistentFilename(transfer: FileTransferRecord): string {
  // Priority 1: Use original metadata name if available
  if (transfer.metadata.name) {
    return transfer.metadata.name;
  }
  
  // Priority 2: Generate descriptive fallback using root hash prefix
  const rootPrefix = transfer.metadata.rootHash?.slice(0, 8) || transfer.rootHash?.slice(0, 8);
  return rootPrefix ? `file_${rootPrefix}` : 'downloaded-file';
}
import {
  useSafeFileTransferWithExtension,
  useAllPeersFileTransfers,
  TransferProgressIndicator,
  TransferStateBadge,
  TransferControlButtons,
  TransferInfoDisplay
} from "./shared-transfer-components";
import { useMobyDB, useSession, useMobyNodeClient } from "../provider";

// Helper functions moved to shared-transfer-components.tsx

export function FileTransferDrawer({ children, open: controlledOpen, onOpenChange: controlledOnOpenChange }: { children: ReactNode | ((props: { unseenCount: number }) => ReactNode); open?: boolean; onOpenChange?: (open: boolean) => void; }) {
  // USE AGGREGATED FILE TRANSFER HOOK FOR MULTI-PEER SUPPORT
  // This aggregates transfers from ALL connected peers, fixing the cross-peer contamination bug
  const aggregatedTransfers = useAllPeersFileTransfers();
  
  // Also get extension capabilities from the safe hook (for UI features)
  const extensionHook = useSafeFileTransferWithExtension();
  
  // Merge aggregated transfers with extension capabilities
  const fileTransfer = {
    activeTransfers: aggregatedTransfers.activeTransfers,
    queuedTransfers: {
      send: aggregatedTransfers.sendQueue,
      receive: aggregatedTransfers.receiveQueue
    },
    completedTransfers: aggregatedTransfers.completedTransfers, // FIX: Add completed transfers
    transferStats: aggregatedTransfers.transferStats,
    startTransfer: aggregatedTransfers.startTransfer,
    pauseTransfer: aggregatedTransfers.pauseTransfer,
    resumeTransfer: aggregatedTransfers.resumeTransfer,
    cancelTransfer: aggregatedTransfers.cancelTransfer,
    hasExtension: extensionHook.hasExtension,
    isChromium: extensionHook.isChromium,
    isEdge: extensionHook.isEdge,
    capabilities: extensionHook.capabilities
  };
  const db = useMobyDB();
  const session = useSession(); // Get current user session for direction detection
  const node = useMobyNodeClient(); // Get node for public key access
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  const setIsOpen = (open: boolean) => {
    setUncontrolledOpen(open);
    controlledOnOpenChange?.(open);
  };
  const [opfsLimit, setOpfsLimit] = useState<number | null>(null);
  const [workerActive, setWorkerActive] = useState(false);
  const [metricsWorkerActive, setMetricsWorkerActive] = useState(false);
  
  // Contact names cache for UI display
  const [contactNames, setContactNames] = useState<Record<string, string>>({});
  
  // Memory monitoring state
  const [memoryUsage, setMemoryUsage] = useState<MemoryUsage | null>(null);
  const [isMemorySupported, setIsMemorySupported] = useState(false);
  const [isMemoryExpanded, setIsMemoryExpanded] = useState(false);

  // Section collapse state — queued and completed start expanded
  const [isQueuedExpanded, setIsQueuedExpanded] = useState(true);
  const [isCompletedExpanded, setIsCompletedExpanded] = useState(true);
  
  // FIX: Pre-compute merkle tree existence to avoid hooks in .map() loop
  // This prevents React Error #185 by maintaining consistent hook count
  const [merkleMap, setMerkleMap] = useState<Map<string, boolean>>(new Map());
  
  // Extract extension properties for convenience (from the unified hook)
  const { hasExtension, isChromium, isEdge, capabilities: extensionCapabilities } = fileTransfer;
  
  // Subscribe to memory usage updates with Firefox fallback support
  useEffect(() => {
    setIsMemorySupported(true);
    
    const unsubscribeMemory = memoryMonitor.subscribe((usage) => {
      setMemoryUsage(usage);
    });
    
    return unsubscribeMemory;
  }, []);
  
  // Fetch contact/group display names for all transfers
  // ONLY runs when drawer opens to avoid infinite loop from Map dependencies
  useEffect(() => {
    if (!isOpen) return; // Only fetch when drawer is visible
    
    const fetchContactNames = async () => {
      const names: Record<string, string> = {};
      const uniqueAddresses = new Set<string>();
      // Track which addresses belong to group transfers for name resolution
      const groupAddresses = new Set<string>();
      
      // Helper to collect addresses from a transfer map
      const collectAddresses = (transfers: Map<string, FileTransferRecord>) => {
        transfers.forEach((transfer) => {
          if (transfer.metadata?.peerAddress) {
            uniqueAddresses.add(transfer.metadata.peerAddress);
            if (transfer.metadata?.isGroupTransfer) groupAddresses.add(transfer.metadata.peerAddress);
          }
          if (transfer.metadata?.recipientAddress) {
            uniqueAddresses.add(transfer.metadata.recipientAddress);
          }
        });
      };
      
      // Collect from all transfer maps including completed
      if (fileTransfer.activeTransfers) collectAddresses(fileTransfer.activeTransfers);
      if (fileTransfer.queuedTransfers) {
        collectAddresses(fileTransfer.queuedTransfers.send);
        collectAddresses(fileTransfer.queuedTransfers.receive);
      }
      if (fileTransfer.completedTransfers) collectAddresses(fileTransfer.completedTransfers);
      
      // Fetch display names for each address
      for (const address of uniqueAddresses) {
        try {
          // For group addresses, resolve from chats table
          if (groupAddresses.has(address)) {
            const chat = await db.chats.get({ address });
            if (chat?.name) {
              names[address] = chat.name;
              continue;
            }
          }
          
          // For P2P/email, resolve from contacts table
          const contact = await db.contacts.get({ address });
          if (contact) {
            names[address] = contact.getDisplayName();
          } else {
            // Fallback: try peer profile nickname from connected peers
            const peer = node.peers.get(address);
            const peerNickname = (peer as any)?.remoteProfile?.nickname;
            if (peerNickname) {
              names[address] = peerNickname;
            } else {
              names[address] = address.slice(0, 8) + '...';
            }
          }
        } catch (error) {
          console.warn(`Failed to fetch name for ${address}:`, error);
          names[address] = address.slice(0, 8) + '...';
        }
      }
      
      setContactNames(names);
    };
    
    fetchContactNames();
  }, [isOpen, db, node]); // Stable dependencies
  
  // PERF FIX: Merkle tree detection optimized to eliminate OPFS polling during active transfer.
  // Previous implementation polled OPFS getFileHandle() every 2 seconds for a cosmetic UI icon,
  // causing main-thread OPFS contention that degraded file transfer throughput.
  // New approach: transfers in active states (transferring/completed/finalizing) always have
  // their tree (stored at chunk 0 reception), so assume true. Only probe OPFS once on drawer
  // open for other states (paused, ready_for_transfer, queued).
  useEffect(() => {
    if (!isOpen) return; // Only check when drawer is visible
    
    let mounted = true;
    
    // States where merkle tree is guaranteed to exist (stored during chunk 0 processing)
    const TREE_GUARANTEED_STATES = new Set(['transferring', 'completed', 'finalizing']);
    
    async function checkMerkleTreesOnce() {
      const map = new Map<string, boolean>();
      
      const allTransfers = [
        ...Array.from(fileTransfer.activeTransfers.values()),
        ...Array.from(fileTransfer.queuedTransfers.send.values()),
        ...Array.from(fileTransfer.queuedTransfers.receive.values()),
        ...Array.from(fileTransfer.completedTransfers?.values() || [])
      ];
      
      // Transfers needing OPFS probe (non-active states only)
      const needsProbe: typeof allTransfers = [];
      
      for (const transfer of allTransfers) {
        if (TREE_GUARANTEED_STATES.has(transfer.state)) {
          // Tree guaranteed to exist after chunk 0 — skip OPFS I/O
          map.set(transfer.sessionId, true);
        } else {
          needsProbe.push(transfer);
        }
      }
      
      // Only hit OPFS for transfers in uncertain states (paused, queued, etc.)
      if (needsProbe.length > 0) {
        try {
          const opfs = await navigator.storage.getDirectory();
          for (const transfer of needsProbe) {
            try {
              await opfs.getFileHandle(`merkle_tree_${transfer.sessionId}`, { create: false });
              map.set(transfer.sessionId, true);
            } catch (error) {
              map.set(transfer.sessionId, false);
            }
          }
        } catch (error) {
          // OPFS not available - all probed transfers have no merkle tree
          console.warn('[MerkleTreeDetection] OPFS not available:', error);
        }
      }
      
      if (mounted) {
        setMerkleMap(map);
      }
    }
    
    // Single check on drawer open — no polling interval
    checkMerkleTreesOnce();
    
    return () => {
      mounted = false;
    };
  }, [isOpen]); // Stable dependency: only re-run when drawer opens/closes

  // Fetch dynamic OPFS limit for UI display
  useEffect(() => {
    let mounted = true;
    extensionCapabilityService
      .getDynamicThresholds()
      .then((thresholds) => {
        if (mounted) setOpfsLimit(thresholds.OPFS_LIMIT);
      })
      .catch((err) => console.warn('[FileTransferDrawer] Failed to fetch dynamic thresholds:', err));
    return () => {
      mounted = false;
    };
  }, []);

  // Check RaptorQ Web Worker availability when drawer opens
  useEffect(() => {
    if (isOpen) {
      setWorkerActive(raptorQService.isUsingWorker());
      setMetricsWorkerActive(peerMetricsService.isWorkerAvailable);
    }
  }, [isOpen]);
  
  // Handle download for completed transfers with extension enhancement
  const handleDownload = useCallback(async (transfer: FileTransferRecord, method: 'opfs' | 'native' = 'opfs') => {
    try {
      console.log(`[DOWNLOAD] Attempting ${method} download of ${transfer.metadata.name} using root hash: ${transfer.metadata.rootHash?.slice(0, 8)}...`);
      
      // Extension-enhanced native download (optional enhancement)
      if (method === 'native' && hasExtension && (window as any).mobyNetExtension?.isAvailable) {
        try {
          const result = await (window as any).mobyNetExtension.postMessage({
            type: 'DOWNLOAD_TO_NATIVE',
            data: {
              rootHash: transfer.metadata.rootHash,
              suggestedName: transfer.metadata.name,
              size: transfer.metadata.size
            }
          });
          
          if (result.success) {
            console.log(`[EXTENSION DOWNLOAD] File saved to: ${result.path}`);
            return;
          } else {
            throw new Error(result.error);
          }
        } catch (error) {
          console.error('[EXTENSION DOWNLOAD] Failed, falling back to OPFS:', error);
          // Fall through to OPFS download
        }
      }
      
      // Standard OPFS download (core functionality - matches working version)
      const opfsFile = await getFileFromOPFS(transfer.metadata.rootHash);
      
      if (!opfsFile) {
        console.error(`[DOWNLOAD] File not found in OPFS for root hash: ${transfer.metadata.rootHash?.slice(0, 8)}...`);
        alert(`File not found in local storage. The file may have been cleaned up or the transfer may not have completed properly.`);
        return;
      }
      
      console.log(`[DOWNLOAD] Successfully retrieved file from OPFS: ${opfsFile.name}, size: ${opfsFile.size}`);
      
      // MEMORY-EFFICIENT STREAMING DOWNLOAD
      // Replace URL.createObjectURL(opfsFile) with streaming approach
      // This prevents memory doubling for large files (e.g., 2GB → 4GB memory usage)
      try {
        await triggerStreamingDownload(transfer.rootHash, getConsistentFilename(transfer));
        console.log(`[DOWNLOAD] Successfully triggered streaming download for: ${transfer.metadata.name}`);
      } catch (streamingError) {
        console.error(`[DOWNLOAD] Streaming download failed, falling back to traditional approach:`, streamingError);
        
        // Fallback to traditional approach if streaming fails
        const url = URL.createObjectURL(opfsFile);
        const link = document.createElement('a');
        link.href = url;
        link.download = getConsistentFilename(transfer);
        
        // Trigger download in user gesture context
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up the URL object
        URL.revokeObjectURL(url);
        
        console.log(`[DOWNLOAD] Fallback download triggered for: ${transfer.metadata.name}`);
      }
    } catch (error) {
      console.error(`[DOWNLOAD] Error downloading file:`, error);
      alert(`Failed to download file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [hasExtension]); // FIX: Added missing dependency
  
  // Extract active and queued transfers from context
  const { activeTransfers, queuedTransfers, completedTransfers, transferStats, pauseTransfer, resumeTransfer, cancelTransfer, startTransfer } = fileTransfer;
  
  // Convert maps to arrays for badge counting
  const activeTransfersArray = Array.from(activeTransfers.values());
  const queuedSendArray = Array.from(queuedTransfers.send.values());
  const queuedReceiveArray = Array.from(queuedTransfers.receive.values());
  // NEW: Include completed transfers with proper type casting
  const completedTransfersArray = completedTransfers 
    ? Array.from(completedTransfers.values()) as FileTransferRecord[]
    : [] as FileTransferRecord[];
  
  // Count unseen transfers (active, queued, and completed)
  // FIX 11D: Deduplicate by sessionId before counting — same sessionId can briefly exist in
  // two maps (e.g., activeTransfers + receiveQueue) during state transitions or reconnect races.
  // Rendering already deduplicates; badge count must too.
  const allTransfersForBadge = [...activeTransfersArray, ...queuedSendArray, ...queuedReceiveArray, ...completedTransfersArray];
  const dedupedForBadge = Array.from(
    new Map(allTransfersForBadge.map(t => [t.sessionId, t])).values()
  );
  const unseenCount = dedupedForBadge.filter(transfer => transfer.custom?.unseen === true).length;
  
  // Clear unseen status when drawer opens
  // FIX: Clear on ORIGINAL transfer objects via node.peers, not just enriched copies.
  // getActiveTransfers() creates new custom objects via spread each enrichment cycle,
  // so clearing on enriched copies is lost on the next cycle — causing badge to re-flash.
  React.useEffect(() => {
    if (isOpen && unseenCount > 0) {
      // Mark all unseen transfers as seen on ORIGINAL transfer objects
      const markTransfersSeen = () => {
        if (!node?.peers) return;
        
        for (const peer of node.peers.values()) {
          // Clear on original activeTransfers
          const peerActive = peer.activeTransfers as Map<string, FileTransferRecord> | undefined;
          if (peerActive) {
            for (const transfer of peerActive.values()) {
              if (transfer.custom?.unseen === true) {
                transfer.custom.unseen = false;
              }
            }
          }
          // Clear on original sendQueue
          const peerSend = peer.sendQueue as Map<string, FileTransferRecord> | undefined;
          if (peerSend) {
            for (const transfer of peerSend.values()) {
              if (transfer.custom?.unseen === true) {
                transfer.custom.unseen = false;
              }
            }
          }
          // Clear on original receiveQueue
          const peerReceive = peer.receiveQueue as Map<string, FileTransferRecord> | undefined;
          if (peerReceive) {
            for (const transfer of peerReceive.values()) {
              if (transfer.custom?.unseen === true) {
                transfer.custom.unseen = false;
              }
            }
          }
          // Clear on original completedTransfers
          const peerCompleted = peer.completedTransfers as Map<string, FileTransferRecord> | undefined;
          if (peerCompleted) {
            for (const transfer of peerCompleted.values()) {
              if (transfer.custom?.unseen === true) {
                transfer.custom.unseen = false;
              }
            }
          }
        }
        
        // Also clear on enriched copies for immediate UI update (prevents 1-frame flash)
        for (const transfer of activeTransfers.values()) {
          if (transfer.custom?.unseen === true) transfer.custom.unseen = false;
        }
        for (const transfer of queuedTransfers.send.values()) {
          if (transfer.custom?.unseen === true) transfer.custom.unseen = false;
        }
        for (const transfer of queuedTransfers.receive.values()) {
          if (transfer.custom?.unseen === true) transfer.custom.unseen = false;
        }
      };
      markTransfersSeen();
    }
  }, [isOpen, unseenCount, activeTransfers, queuedTransfers, node]);
  
  // Use already computed arrays for rendering
  
  // Sort transfers by lastUpdated timestamp (newest first)
  const sortByTimestamp = (a: FileTransferRecord, b: FileTransferRecord) => b.lastUpdated - a.lastUpdated;

  // ACTIVE: non-terminal TRANSFERRING + FINALIZING + PREPARING + METADATA states
  // QUEUED: READY_FOR_TRANSFER, METADATA_RECEIVED, METADATA_PENDING, METADATA_SENT, PAUSED, ERROR, FAILED, CANCELLED
  // COMPLETED: COMPLETED state only
  // Deduplication: track seen sessionIds across all three groups so a transfer in a
  // state-transition race (briefly in two maps) is only rendered once.
  const seenSessionIds = new Set<string>();

  const ACTIVE_STATES = new Set<FileTransferStateEnum>([
    FileTransferStateEnum.TRANSFERRING,
    FileTransferStateEnum.FINALIZING,
    FileTransferStateEnum.PREPARING,
    FileTransferStateEnum.METADATA_PENDING,
    FileTransferStateEnum.METADATA_SENT,
    FileTransferStateEnum.METADATA_RECEIVED,
    FileTransferStateEnum.PENDING_CONSENT,
  ]);

  const dedup = (arr: FileTransferRecord[]): FileTransferRecord[] =>
    arr.filter(t => { if (seenSessionIds.has(t.sessionId)) return false; seenSessionIds.add(t.sessionId); return true; });

  // Active: from activeTransfers map — filter to genuinely active states
  const sortedActiveTransfers = dedup(
    [...activeTransfersArray].sort(sortByTimestamp).filter(t => ACTIVE_STATES.has(t.state))
  );

  // Queued: send + receive queues, plus anything in activeTransfers that isn't active-state
  // (e.g. PAUSED, READY_FOR_TRANSFER, ERROR slots that landed in activeTransfers)
  const queuedOverflow = activeTransfersArray
    .filter(t => !seenSessionIds.has(t.sessionId) && !ACTIVE_STATES.has(t.state) && t.state !== FileTransferStateEnum.COMPLETED);
  const sortedQueuedTransfers = dedup(
    [...queuedSendArray, ...queuedReceiveArray, ...queuedOverflow].sort(sortByTimestamp)
      .filter(t => t.state !== FileTransferStateEnum.COMPLETED)
  );

  // Completed: from completedTransfers map + any completed that leaked into other maps
  const completedOverflow = [...activeTransfersArray, ...queuedSendArray, ...queuedReceiveArray]
    .filter(t => !seenSessionIds.has(t.sessionId) && t.state === FileTransferStateEnum.COMPLETED);
  const sortedCompletedTransfers = dedup(
    [...completedTransfersArray, ...completedOverflow].sort(sortByTimestamp)
  );

  // Flat list for badge/seat counting (all three sections combined)
  const allTransfers = [...sortedActiveTransfers, ...sortedQueuedTransfers, ...sortedCompletedTransfers];
  
  // SEAT BUDGET: Compute occupied seats from TRANSFERRING transfers
  // TOTAL_PIPELINE_BUDGET = 3 (global, per peer, across P2P/Email/Group forks)
  const TOTAL_SEATS = 3;
  const transferringTransfers = allTransfers.filter(t => t.state === FileTransferStateEnum.TRANSFERRING);
  const recvSeats = transferringTransfers.filter(t => t.metadata?.direction === 'inbound').length;
  const sendSeats = transferringTransfers.filter(t => t.metadata?.direction === 'outbound').length;
  const occupiedSeats = Math.min(recvSeats + sendSeats, TOTAL_SEATS);

  // If no transfers and no context, don't render the full drawer content
  const hasTransfers = allTransfers.length > 0;
  
  return (
    <mui.Drawer.Root open={isOpen} onOpenChange={setIsOpen} direction="left">
      {controlledOpen === undefined ? (
        <mui.Drawer.Trigger asChild>
          {typeof children === 'function' ? children({ unseenCount }) : children}
        </mui.Drawer.Trigger>
      ) : (
        typeof children === 'function' ? children({ unseenCount }) : children
      )}
      <mui.Drawer.Portal>
        <mui.Drawer.Overlay className="sm:bg-transparent" />
        <mui.Drawer.Content className="bg-surface-container-high text-on-surface fixed inset-y-0 left-0 z-50 flex h-full w-full flex-col border-r border-outline sm:ml-20 sm:w-80 overflow-hidden">
          {/* Pointer arrow — visible on desktop */}
          <div className="hidden sm:block absolute -left-2 top-6 w-0 h-0 border-y-[8px] border-y-transparent border-r-[8px] border-r-surface-container-high" />
          <div className="border-b-outline flex items-center justify-between border-b p-4">
            <mui.Drawer.Title className="text-headline-sm">MobyFiles™</mui.Drawer.Title>
            <button onClick={() => setIsOpen(false)} className="sm:hidden p-1 rounded-full hover:bg-on-surface-variant/8" aria-label="Close drawer">
              <XIcon size={20} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            
            {/* Extension Status Indicator */}
            <div className="flex items-center gap-2 mb-2">
              <div className="flex items-center gap-1">
                {hasExtension ? (
                  <>
                    <Zap size={14} className="text-green-600" />
                    <span className="text-green-700 font-medium text-sm">Extension Mode</span>
                  </>
                ) : (
                  <>
                    <Globe size={14} className="text-blue-600" />
                    <span className="text-blue-700 font-medium text-sm">Browser Mode</span>
                  </>
                )}
              </div>
              
              {/* Worker Status Indicator */}
              <div className="flex items-center gap-1">
                <Pickaxe size={13} className={workerActive ? "text-green-600" : "text-gray-400"} />
                <ChartNetwork size={13} className={metricsWorkerActive ? "text-green-600" : "text-gray-400"} />
                <span className={`text-xs font-medium ${workerActive || metricsWorkerActive ? "text-green-700" : "text-gray-400"}`}>
                  {workerActive || metricsWorkerActive ? "Worker" : "Main Thread"}
                </span>
              </div>
            </div>

            {/* Seat Budget Indicator */}
            {occupiedSeats > 0 && (
              <div className={`flex items-center gap-1.5 mb-1 text-xs font-medium ${
                occupiedSeats >= TOTAL_SEATS ? 'text-amber-600' : 'text-on-surface-variant'
              }`}>
                <Activity size={12} />
                <span>Seats: {occupiedSeats}/{TOTAL_SEATS}</span>
                {recvSeats > 0 && sendSeats > 0 && (
                  <span className="text-on-surface-variant/60">({recvSeats} recv + {sendSeats} send)</span>
                )}
                {occupiedSeats >= TOTAL_SEATS && (
                  <span className="text-amber-600">— new transfers will queue</span>
                )}
              </div>
            )}

            <p className="text-body-md text-on-secondary-container">
              {`${transferStats.totalActive} active, ${transferStats.totalQueued} queued, ${transferStats.completedCount || 0} completed`}
            </p>

            {/* Memory Usage Dropdown */}
            {isMemorySupported && memoryUsage && (
              <div className="mt-4">
                {/* Dropdown Header - Always Visible */}
                <button
                  onClick={() => setIsMemoryExpanded(!isMemoryExpanded)}
                  className={`w-full p-2 rounded-lg border transition-colors ${
                    memoryUsage.isCriticalUsage ? 'bg-red-50 border-red-200 hover:bg-red-100' :
                    memoryUsage.isHighUsage ? 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100' :
                    'bg-green-50 border-green-200 hover:bg-green-100'
                  }`}
                  aria-label="Toggle memory usage details"
                  aria-expanded={isMemoryExpanded ? "true" : "false"}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {memoryUsage.isCriticalUsage ? (
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                      ) : memoryUsage.isHighUsage ? (
                        <AlertTriangle className="h-4 w-4 text-yellow-500" />
                      ) : (
                        <Activity className="h-4 w-4 text-green-500" />
                      )}
                      <span className="text-sm font-medium">Memory Usage</span>
                      <span className={`text-xs font-medium ${
                        memoryUsage.isCriticalUsage ? 'text-red-600' :
                        memoryUsage.isHighUsage ? 'text-yellow-600' :
                        'text-green-600'
                      }`}>
                        {memoryUsage.usagePercentage.toFixed(0)}%
                      </span>
                    </div>
                    {isMemoryExpanded ? (
                      <ChevronUp className="h-4 w-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-500" />
                    )}
                  </div>
                  
                  {/* Compact progress bar - always visible */}
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                    <div 
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        memoryUsage.isCriticalUsage ? 'bg-red-500' :
                        memoryUsage.isHighUsage ? 'bg-yellow-500' :
                        'bg-green-500'
                      }`}
                      // eslint-disable-next-line style/no-inline-styles -- Dynamic width requires inline style
                      style={{ width: `${Math.min(memoryUsage.usagePercentage, 100)}%` }}
                    />
                  </div>
                </button>
                
                {/* Expandable Content */}
                {isMemoryExpanded && (
                  <div className={`mt-1 p-3 rounded-lg border ${
                    memoryUsage.isCriticalUsage ? 'bg-red-50 border-red-200' :
                    memoryUsage.isHighUsage ? 'bg-yellow-50 border-yellow-200' :
                    'bg-green-50 border-green-200'
                  }`}>
                    <div className="text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-600">JS Heap Used:</span>
                        <span className={`font-medium ${memoryMonitor.getMemoryStatusColor(memoryUsage)}`}>
                          {memoryUsage.usedMB.toFixed(1)}MB
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Available:</span>
                        <span className="font-medium">{(memoryUsage.limitMB - memoryUsage.usedMB).toFixed(1)}MB</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Limit:</span>
                        <span className="font-medium">{memoryUsage.limitMB.toFixed(1)}MB</span>
                      </div>
                      {opfsLimit !== null && (
                        <div className="flex justify-between pt-1 border-t border-gray-200">
                          <span className="text-gray-600">OPFS Capacity:</span>
                          <span className="font-medium">{formatBytes(opfsLimit)}</span>
                        </div>
                      )}
                    </div>
                
                    {/* Warnings */}
                    {memoryUsage.isCriticalUsage && (
                      <div className="mt-2 text-xs text-red-600">
                        ⚠️ Critical memory usage - consider closing other tabs
                      </div>
                    )}
                    {memoryUsage.isHighUsage && !memoryUsage.isCriticalUsage && (
                      <div className="mt-2 text-xs text-yellow-600">
                        ⚠️ High memory usage - large files may fail
                      </div>
                    )}
                    
                    {/* Extension Enhancement Status */}
                    {isChromium && (
                      <div className="mt-3 pt-2 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {hasExtension ? (
                          <>
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            <span className="text-xs font-medium text-green-700">Extension Active</span>
                          </>
                        ) : (
                          <>
                            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                            <span className="text-xs font-medium text-gray-600">Browser Mode</span>
                          </>
                        )}
                      </div>
                      
                      {extensionCapabilities && (
                        <div className="text-xs text-gray-500">
                          Max: {Math.round(extensionCapabilities.maxFileSize / (1024 * 1024 * 1024))}GB
                        </div>
                      )}
                    </div>
                    
                    {/* Large File Recommendation */}
                    {!hasExtension && (memoryUsage.isHighUsage || memoryUsage.isCriticalUsage) && (
                      <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
                        <div className="font-medium text-blue-800">💡 Need larger file support?</div>
                        <div className="text-blue-700 mt-1">
                          Install the MobyNet Extension for enhanced performance features
                        </div>
                      </div>
                    )}
                    
                    {/* Extension Capabilities Summary */}
                    {hasExtension && extensionCapabilities && (
                      <div className="mt-2 text-xs text-green-700 space-y-1">
                        <div className="flex justify-between">
                          <span>Worker Pool:</span>
                          <span>{extensionCapabilities.hasWorkerPool ? '✓ Active' : '✗ Inactive'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Native FS:</span>
                          <span>{extensionCapabilities.hasNativeFileSystem ? '✓ Available' : '✗ Limited'}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                  </div>
                )}
              </div>
            )}

            <div className="-mx-7 mt-4 flex flex-1 flex-col overflow-y-auto">
              {allTransfers.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-on-secondary-container">
                  No file transfers in progress
                </div>
              ) : (
                (() => {
                // Helper: render a single transfer item (shared across all three sections)
                const renderTransferItem = (transfer: FileTransferRecord) => {
                  // Determine if this is an upload or download
                  // Primary method: use metadata.direction if available
                  // 'outbound' means we're sending (upload), 'inbound' means we're receiving (download)
                  let direction: 'upload' | 'download';
                  
                  if (transfer.metadata?.direction) {
                    direction = transfer.metadata.direction === 'outbound' ? 'upload' : 'download';
                  } else {
                    // CRITICAL FIX: peerAddress = SENDER's public key
                    // If peerAddress matches OUR public key, WE are sending (upload)
                    // If peerAddress is different, the REMOTE peer is sending, so WE are receiving (download)
                    const isSender = transfer.metadata?.peerAddress === node.publicKey;
                    direction = isSender ? 'upload' : 'download';
                  }
                  
                  // LIGHTWEIGHT: Calculate progress from merkle tree counter
                  const completedChunks = transfer.metadata?.completedChunks || 0;
                  const totalChunks = transfer.metadata?.totalChunks || 0;
                  const progress = totalChunks > 0 
                    ? Math.round((completedChunks / totalChunks) * 100) 
                    : 0;
                  
                  // Format start time - use stored transferStartTime if available, otherwise metadata creation timestamp
                  const startedAt = formatTimestamp(transfer.custom?.transferStartTime || transfer.metadata?.timestamp || transfer.lastUpdated);
                  
                  let transferRate = 'preparing...';
                  let timeRemaining = 'estimating...';
                  const fileSize = transfer.metadata?.size || 0;
                  
                  if (transfer.state === FileTransferStateEnum.COMPLETED) {
                    transferRate = 'completed';
                    timeRemaining = 'completed';
                  } else if (transfer.state === FileTransferStateEnum.PAUSED) {
                    // Show progress for paused transfers (e.g., "paused at 33%")
                    transferRate = progress > 0 ? `paused at ${progress}%` : 'paused';
                    timeRemaining = progress > 0 ? `resume from ${progress}%` : 'ready to resume';
                  } else if (transfer.state === FileTransferStateEnum.TRANSFERRING) {
                    // UNIFIED: Both sender and receiver use completedChunks × chunkSize / elapsed.
                    // Previous differential rate + exponential smoothing was overcomplicated and
                    // produced wildly inflated receiver speeds (~100 MB/s on a 3 MB/s link)
                    // because integer percentage quantization caused 145MB byte jumps per 1% tick.
                    const chunkSizeBytes = transfer.metadata?.chunkSize || transfer.custom?.chunkSize || 16 * 1024 * 1024;
                    const bytesTransferred = Math.min(completedChunks * chunkSizeBytes, fileSize);
                    
                    const transferStartTime = transfer.custom?.transferStartTime || transfer.metadata?.timestamp || 0;
                    const timeElapsed = transferStartTime > 0 ? Date.now() - transferStartTime : 0;
                    const elapsedSeconds = timeElapsed / 1000;
                    
                    if (elapsedSeconds > 1 && bytesTransferred > 0) {
                      const bytesPerSecond = bytesTransferred / elapsedSeconds;
                      const remainingBytes = fileSize - bytesTransferred;
                      
                      if (bytesPerSecond > 1000) {
                        transferRate = `${formatBytes(bytesPerSecond)}/s`;
                        
                        if (remainingBytes > 0 && progress < 99) {
                          const secondsRemaining = remainingBytes / bytesPerSecond;
                          if (secondsRemaining > 0 && secondsRemaining < 86400) {
                            timeRemaining = formatTimeRemaining(secondsRemaining * 1000);
                          } else if (secondsRemaining >= 86400) {
                            timeRemaining = 'over 24 hours';
                          } else {
                            timeRemaining = 'calculating...';
                          }
                        } else {
                          timeRemaining = 'finishing...';
                        }
                      } else {
                        transferRate = 'starting...';
                        timeRemaining = 'calculating...';
                      }
                    } else if (elapsedSeconds <= 1 && completedChunks > 0) {
                      transferRate = 'starting...';
                      timeRemaining = 'calculating...';
                    } else {
                      transferRate = 'connecting...';
                      timeRemaining = 'connecting...';
                    }
                  } else {
                    // For non-transferring states, show appropriate status
                    transferRate = transfer.state === FileTransferStateEnum.READY_FOR_TRANSFER ? 'ready' :
                                  transfer.state === FileTransferStateEnum.METADATA_PENDING ? 'preparing...' :
                                  transfer.state === FileTransferStateEnum.FAILED ? 'failed' :
                                  'waiting...';
                  }
                  
                  // FIX: Use correct address for username display
                  // Upload (outbound): Show "To: <receiver name>" using recipientAddress
                  // Download (inbound): Show "From: <sender name>" using peerAddress
                  const displayAddress = direction === 'upload' 
                    ? (transfer.metadata.recipientAddress || transfer.metadata.peerAddress)
                    : transfer.metadata.peerAddress;
                  const username = displayAddress 
                    ? (contactNames[displayAddress] || displayAddress.slice(0, 8) + '...') 
                    : 'Unknown peer';
                  
                  // Fork type detection for badge labels
                  const forkType: 'P2P' | 'Group' | 'Email' = 
                    transfer.metadata?.isGroupTransfer ? 'Group' :
                    (transfer.metadata?.emailInitiated || transfer.custom?.emailInitiated) ? 'Email' :
                    'P2P';

                  return (
                    <FileTransferListItem
                      key={transfer.sessionId}
                      sessionId={transfer.sessionId}
                      filename={transfer.metadata.name || 'Unknown file'}
                      forkType={forkType}
                      size={formatBytes(transfer.metadata.size)}
                      progress={progress}
                      transferRate={transferRate}
                      timeRemaining={timeRemaining}
                      direction={direction}
                      startedAt={startedAt}
                      username={username}
                      state={transfer.state}
                      networkQuality={transfer.custom?.networkQuality || 'unknown'}
                      bandwidth={transfer.custom?.webrtcBandwidth}
                      chunkSize={transfer.custom?.chunkSize}
                      hasMerkleTree={merkleMap.get(transfer.sessionId) || false}
                      pausedBy={transfer.custom?.pausedBy}
                      completedAt={transfer.custom?.completedAt}
                      errorMessage={transfer.errorMessage || transfer.metadata?.error}
                      onPause={() => {
                        fileTransfer.pauseTransfer(transfer.sessionId);
                        return Promise.resolve();
                      }}
                      onResume={() => {
                        fileTransfer.resumeTransfer(transfer.sessionId);
                        return Promise.resolve();
                      }}
                      onCancel={() => {
                        fileTransfer.cancelTransfer(transfer.sessionId);
                        return Promise.resolve();
                      }}
                      onStart={() => {
                        fileTransfer.startTransfer(transfer.sessionId);
                        return Promise.resolve();
                      }}
                      onDownload={() => {
                        handleDownload(transfer, 'opfs');
                        return Promise.resolve();
                      }}
                      hasExtension={hasExtension}
                      onDownloadNative={hasExtension ? () => {
                        handleDownload(transfer, 'native');
                        return Promise.resolve();
                      } : undefined}
                    />
                  );
                };

                return (
                  <>
                    {/* ── ACTIVE ────────────────────────────────────────────── */}
                    {sortedActiveTransfers.length > 0 && (
                      <>
                        <div className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-on-surface-variant bg-surface-container-high sticky top-0 z-10">
                          Active · {sortedActiveTransfers.length}
                        </div>
                        {sortedActiveTransfers.map(renderTransferItem)}
                      </>
                    )}

                    {/* ── QUEUED ────────────────────────────────────────────── */}
                    {sortedQueuedTransfers.length > 0 && (
                      <>
                        <button
                          onClick={() => setIsQueuedExpanded(v => !v)}
                          className="w-full flex items-center justify-between px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-on-surface-variant bg-surface-container-high hover:bg-on-surface-variant/8 transition-colors sticky top-0 z-10"
                        >
                          <span>Queued · {sortedQueuedTransfers.length}</span>
                          {isQueuedExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                        {isQueuedExpanded && sortedQueuedTransfers.map(renderTransferItem)}
                      </>
                    )}

                    {/* ── COMPLETED ─────────────────────────────────────────── */}
                    {sortedCompletedTransfers.length > 0 && (
                      <>
                        <button
                          onClick={() => setIsCompletedExpanded(v => !v)}
                          className="w-full flex items-center justify-between px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-on-surface-variant bg-surface-container-high hover:bg-on-surface-variant/8 transition-colors sticky top-0 z-10"
                        >
                          <span>Completed · {sortedCompletedTransfers.length}</span>
                          {isCompletedExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                        {isCompletedExpanded && sortedCompletedTransfers.map(renderTransferItem)}
                      </>
                    )}
                  </>
                );
                })()
              )}
            </div>
          </div>
        </mui.Drawer.Content>
      </mui.Drawer.Portal>
    </mui.Drawer.Root>
  );
}

function FileTransferListItem({
  filename,
  forkType,
  size,
  progress,
  transferRate,
  timeRemaining,
  direction,
  startedAt,
  username,
  sessionId,
  state,
  networkQuality,
  bandwidth,
  chunkSize,
  hasMerkleTree,
  pausedBy,
  completedAt,
  errorMessage,
  onPause,
  onResume,
  onCancel,
  onStart,
  onDownload,
  hasExtension,
  onDownloadNative
}: {
  filename: string;
  forkType?: 'P2P' | 'Group' | 'Email';
  size: string;
  progress: number;
  transferRate: string;
  timeRemaining: string;
  direction: string;
  startedAt: string;
  username: string;
  sessionId: string;
  state: FileTransferStateEnum;
  networkQuality?: 'poor' | 'fair' | 'good' | 'excellent' | 'unknown';
  bandwidth?: number;
  chunkSize?: number;
  hasMerkleTree?: boolean;
  pausedBy?: 'local' | 'peer' | 'disconnect';
  completedAt?: number;
  errorMessage?: string;
  onPause?: () => Promise<void>;
  onResume?: () => Promise<void>;
  onCancel?: () => Promise<void>;
  onStart?: () => Promise<void>;
  onDownload?: () => Promise<void>;
  hasExtension?: boolean;
  onDownloadNative?: () => Promise<void>;
}) {
  return (
    <div className="border-b-outline-variant flex items-center gap-3 [border-bottom-width:1px] px-4 py-4">
      {/* Progress indicator */}
      <TransferProgressIndicator progress={progress} size={40} />

      <div className="flex-1">
        <div className="text-title-sm text-on-primary-container flex items-center gap-1.5">
          {forkType && forkType !== 'P2P' && (
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none ${
              forkType === 'Group' ? 'bg-indigo-100 text-indigo-700' : 'bg-teal-100 text-teal-700'
            }`}>{forkType}</span>
          )}
          <span className="truncate">{filename}</span>
          <span className="shrink-0 text-on-surface-variant">- {size}</span>
        </div>
        
        {/* Enhanced transfer info with network quality metrics */}
        <TransferInfoDisplay
          transferRate={transferRate}
          timeRemaining={timeRemaining}
          direction={direction as "upload" | "download"}
          username={username}
          startedAt={startedAt}
          networkQuality={networkQuality}
          // FIX: Don't show bandwidth/chunkSize for completed transfers (UI cleanup)
          bandwidth={state === FileTransferStateEnum.COMPLETED ? undefined : bandwidth}
          chunkSize={state === FileTransferStateEnum.COMPLETED ? undefined : chunkSize}
          adaptiveMode={true}
        />
        
        <div className="flex items-center gap-1 mt-1">
          {/* Visual state indicator */}
          <TransferStateBadge state={state} />
          
          {/* Show pause reason if paused */}
          {state === FileTransferStateEnum.PAUSED && pausedBy && (
            <span className="text-xs text-on-surface-variant ml-2">
              {pausedBy === 'local' ? 'Paused by you' : 'Paused by peer'}
            </span>
          )}
          
          {/* Show completion timestamp if completed */}
          {state === FileTransferStateEnum.COMPLETED && completedAt && (
            <span className="text-xs text-on-surface-variant ml-2">
              · {formatTimestamp(completedAt)}
            </span>
          )}
          
          {/* Show error message if failed */}
          {(state === FileTransferStateEnum.FAILED || state === FileTransferStateEnum.ERROR) && errorMessage && (
            <span className="text-xs text-error ml-2" title={errorMessage}>
              · {errorMessage.length > 50 ? errorMessage.slice(0, 50) + '...' : errorMessage}
            </span>
          )}
        </div>
        
        {/* Action buttons moved below state indicator */}
        <div className="flex gap-2 mt-2">
          {/* Show loading indicator for FINALIZING state */}
          {state === FileTransferStateEnum.FINALIZING ? (
            <div className="flex items-center gap-2 text-sm text-on-surface-variant">
              <LoaderCircle size={16} className="animate-spin" />
              <span>Finalizing file...</span>
            </div>
          ) : state === FileTransferStateEnum.COMPLETED && onDownload ? (
            /* Show download buttons + dismiss X for COMPLETED transfers */
            <div className="flex gap-1">
              {/* Standard OPFS Download */}
              <button 
                onClick={() => onDownload?.()}
                className="group relative grid shrink-0 place-items-center rounded-full p-2 hover:cursor-pointer text-primary"
                title="Quick download (Browser storage)"
              >
                <span
                  aria-hidden
                  className="group-hover:bg-on-surface-variant/8 group-data-pressed:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
                />
                <DownloadIcon size={16} />
              </button>
              
              {/* Extension-Enhanced Native Download */}
              {hasExtension && onDownloadNative && (
                <button 
                  onClick={() => onDownloadNative?.()}
                  className="group relative grid shrink-0 place-items-center rounded-full p-2 hover:cursor-pointer text-green-600"
                  title="Save to computer (Extension enhanced)"
                >
                  <span
                    aria-hidden
                    className="group-hover:bg-green-50 group-data-pressed:bg-green-100 absolute inset-0 rounded-full transition"
                  />
                  <HardDrive size={16} />
                </button>
              )}
              
              {/* Merkle Tree Indicator - Shows when tree exists in OPFS */}
              {hasMerkleTree && (
                <button 
                  className="group relative grid shrink-0 place-items-center rounded-full p-2 hover:cursor-pointer text-emerald-600"
                  title="Merkle tree available (NFT receipt, group seeding)"
                >
                  <span
                    aria-hidden
                    className="group-hover:bg-emerald-50 group-data-pressed:bg-emerald-100 absolute inset-0 rounded-full transition"
                  />
                  <Trees size={16} />
                </button>
              )}

              {/* Dismiss X — always available to clear completed entry from drawer */}
              {onCancel && (
                <button
                  onClick={() => onCancel?.()}
                  className="group relative grid shrink-0 place-items-center rounded-full p-2 hover:cursor-pointer text-on-surface-variant"
                  title="Dismiss (removes local copy)"
                >
                  <span
                    aria-hidden
                    className="group-hover:bg-on-surface-variant/8 group-data-pressed:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
                  />
                  <XIcon size={16} />
                </button>
              )}
            </div>
          ) : (
            /* Control buttons for other transfer states */
            <div className="flex gap-1">
              <TransferControlButtons
                state={state}
                sessionId={sessionId}
                pausedBy={pausedBy}
                direction={direction === 'upload' ? 'outbound' : 'inbound'}
                onPause={onPause}
                onResume={onResume}
                onCancel={onCancel}
                onStart={onStart}
                size={16}  // Increased from 10 to match download buttons
              />
              
              {/* Merkle Tree Indicator - Shows for active/paused transfers too */}
              {hasMerkleTree && (
                <button 
                  className="group relative grid shrink-0 place-items-center rounded-full p-2 hover:cursor-pointer text-emerald-600"
                  title="Merkle tree available (Cryptographic validation active)"
                >
                  <span
                    aria-hidden
                    className="group-hover:bg-emerald-50 group-data-pressed:bg-emerald-100 absolute inset-0 rounded-full transition"
                  />
                  <Trees size={16} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
